import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { PostCard } from './PostCard';
import type { Post, PostAssignments, ScheduleResult, SchedulingProgress } from '../types';

interface SchedulingProgressProps {
  posts: Post[];
  postAssignments: PostAssignments;
  scheduleResults: ScheduleResult[];
  progress: SchedulingProgress;
}

export function SchedulingProgress({
  posts,
  postAssignments,
  scheduleResults,
  progress,
}: SchedulingProgressProps) {
  // Get all assigned posts in order
  const assignedPostIds = Object.values(postAssignments).flat();
  const assignedPosts = posts.filter((post) => assignedPostIds.includes(post.id));

  const progressPercent =
    progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Scheduling Posts...
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Scheduling to your social media accounts via Kontentino
        </p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="text-sm text-center text-muted-foreground">
          {progress.completed} of {progress.total} posts scheduled
        </div>
      </div>

      {/* Post list */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {assignedPosts.map((post, idx) => {
          const result = scheduleResults.find((r) => r.postId === post.id);
          const isCompleted = !!result;
          const isCurrent = idx === progress.currentIndex && !isCompleted;
          const isPending = idx > progress.currentIndex;

          return (
            <div
              key={post.id}
              className={`flex items-center gap-3 p-3 rounded transition-colors ${
                isCurrent ? 'bg-primary/10' : ''
              }`}
            >
              <div className="flex-shrink-0">
                {isCompleted && result.success && (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                )}
                {isCompleted && !result.success && (
                  <XCircle className="h-5 w-5 text-destructive" />
                )}
                {isCurrent && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                {isPending && <div className="h-5 w-5 rounded-full bg-muted" />}
              </div>

              <div className="flex-1 min-w-0">
                <PostCard post={post} compact />
                {isCompleted && result.success && (
                  <div className="text-xs text-green-600 mt-1">Scheduled successfully</div>
                )}
                {isCompleted && !result.success && (
                  <div className="text-xs text-destructive mt-1">
                    Failed: {result.error || 'Unknown error'}
                  </div>
                )}
                {isCurrent && <div className="text-xs text-primary mt-1">Scheduling...</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
